import React, { useState, useEffect } from "react";
import axios from "axios";
import {
    Container, TextField, Button, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, Paper, Snackbar, Alert, IconButton, Grid, styled, Box,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Typography } from '@mui/material';

const API_URL = window.location.hostname === 'localhost' ? 'http://localhost:5400' : 'https://crmb.apitrading.lk';

const StyledContainer = styled(Container)(({ theme }) => ({
    padding: theme.spacing(3),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    boxShadow: "0px 3px 5px rgba(0,0,0,0.2)",
    borderRadius: "10px",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const StyledButton = styled(Button)(({ theme }) => ({
    padding: theme.spacing(1.5),
    fontWeight: "bold",
    borderRadius: "8px",
    background: 'linear-gradient(45deg, #F15A24, #e52e71)',
    color: 'white',
    '&:hover': {
        opacity: 0.8,
    },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    boxShadow: "0px 2px 4px rgba(0,0,0,0.1)",
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: "bold",
}));

const StyledAlert = styled(Alert)(({ theme }) => ({
    borderRadius: "8px",
}));

const StyledTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    fontFamily: 'Arial, sans-serif',
    marginBottom: theme.spacing(2),
}));


export default function SuperAdminProfile() {
    const [superAdmin, setSuperAdmin] = useState({ name: "", email: "", password: "" });
    const [admins, setAdmins] = useState([]);
    const [newAdmin, setNewAdmin] = useState({ name: "", email: "", password: "" });
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [editAdmin, setEditAdmin] = useState(null);
    const [passwordVisibility, setPasswordVisibility] = useState({});
    const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, email: null, name: null });

    // Fetch super admin details and admin list
    useEffect(() => {
        axios.get(`${API_URL}/auth/superadmin/details`)
            .then((res) => {
                setSuperAdmin(res.data.superAdmin);
                setAdmins(res.data.admins);
            })
            .catch((err) => console.error("❌ Error fetching details:", err));
    }, []);

    // Handle snackbar close
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Toggle Password Visibility
    const togglePasswordVisibility = (email) => {
        setPasswordVisibility(prev => ({ ...prev, [email]: !prev[email] }));
    };

    // Handle changes for super admin form
    const handleSuperAdminChange = (e) => {
        setSuperAdmin({ ...superAdmin, [e.target.name]: e.target.value });
    };

    // Handle changes for new admin form
    const handleNewAdminChange = (e) => {
        setNewAdmin({ ...newAdmin, [e.target.name]: e.target.value });
    };

    // Handle changes for updating existing admin
    const handleEditAdminChange = (e) => {
        setEditAdmin({ ...editAdmin, [e.target.name]: e.target.value });
    };

    // Update super admin details
    const updateSuperAdmin = () => {
        axios.put(`${API_URL}/auth/superadmin/update`, superAdmin)
            .then(() => setSnackbar({ open: true, message: "✅ Super Admin Updated!", severity: "success" }))
            .catch(() => setSnackbar({ open: true, message: "❌ Update Failed!", severity: "error" }));
    };

    // Add a new admin
    const addAdmin = () => {
        axios.post(`${API_URL}/auth/admin/add`, newAdmin)
            .then(() => {
                setSnackbar({ open: true, message: "✅ Admin Added!", severity: "success" });
                setAdmins([...admins, newAdmin]);
                setNewAdmin({ name: "", email: "", password: "" });
            })
            .catch(() => setSnackbar({ open: true, message: "❌ Error adding admin!", severity: "error" }));
    };

    // Update existing admin password
    const updateAdminPassword = () => {
        axios.put(`${API_URL}/auth/superadmin/updateadmin`, editAdmin)
            .then(() => {
                setSnackbar({ open: true, message: "✅ Admin Password Updated!", severity: "success" });
                setAdmins(admins.map(admin => admin.email === editAdmin.email ? editAdmin : admin));
                setEditAdmin(null);
            })
            .catch(() => setSnackbar({ open: true, message: "❌ Update Failed!", severity: "error" }));
    };

    // Delete an admin
    const deleteAdmin = async (email) => {
        try {
            await axios.delete(`${API_URL}/auth/admin/delete/${email}`);
            setSnackbar({ open: true, message: "✅ Admin Deleted!", severity: "success" });
            setAdmins(admins.filter(admin => admin.email !== email));
        } catch (error) {
            console.error("❌ Error deleting admin:", error);
            setSnackbar({ open: true, message: "❌ Error deleting admin!", severity: "error" });
        }
    };

    const handleOpenDeleteConfirmation = (email, name) => {
        setDeleteConfirmation({ open: true, email: email, name: name });
    };

    const handleCloseDeleteConfirmation = () => {
        setDeleteConfirmation({ open: false, email: null, name: null });
    };

    const handleConfirmDelete = async () => {
        try {
            await axios.delete(`${API_URL}/auth/admin/delete/${deleteConfirmation.email}`);
            setSnackbar({ open: true, message: "✅ Admin Deleted!", severity: "success" });
            setAdmins(admins.filter(admin => admin.email !== deleteConfirmation.email));
        } catch (error) {
            console.error("❌ Error deleting admin:", error);
            setSnackbar({ open: true, message: "❌ Error deleting admin!", severity: "error" });
        }
        handleCloseDeleteConfirmation();
    };

    return (
        <StyledContainer maxWidth="xl">
            <Grid container spacing={3}>
                {/* Left Side - Super Admin Details */}
                <Grid item xs={12} md={4}>
                    <StyledPaper>
                        <StyledTitle variant="h6" gutterBottom>Edit Your Details</StyledTitle>
                        <StyledTextField label="Name" name="name" fullWidth margin="normal" value={superAdmin.name} onChange={handleSuperAdminChange} />
                        <StyledTextField label="Email" name="email" fullWidth margin="normal" value={superAdmin.email} onChange={handleSuperAdminChange} />
                        <StyledTextField label="New Password" name="password" type="password" fullWidth margin="normal" value={superAdmin.password} onChange={handleSuperAdminChange} />
                        <StyledButton variant="contained" color="primary" onClick={updateSuperAdmin} fullWidth
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'primary.dark',
                                    color: '#000',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                                },
                                transition: 'all 0.3s ease'
                            }}
                        >Update</StyledButton>
                    </StyledPaper>
                </Grid>

                {/* Right Side - Admin Management */}
                <Grid item xs={12} md={8}>
                    <StyledPaper>
                        <StyledTitle variant="h6" gutterBottom>Add New Admin</StyledTitle>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <StyledTextField label="Admin Name" name="name" fullWidth margin="normal" value={newAdmin.name} onChange={handleNewAdminChange} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <StyledTextField label="Admin Email" name="email" fullWidth margin="normal" value={newAdmin.email} onChange={handleNewAdminChange} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <StyledTextField label="Admin Password" name="password" type="password" fullWidth margin="normal" value={newAdmin.password} onChange={handleNewAdminChange} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <StyledButton variant="contained" color="secondary" onClick={addAdmin} fullWidth
                                    sx={{
                                        mt: 3,
                                        '&:hover': {
                                            backgroundColor: 'primary.dark',
                                            color: '#000',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                                        },
                                        transition: 'all 0.3s ease'
                                    }}>Add Admin</StyledButton>
                            </Grid>
                        </Grid>
                    </StyledPaper>

                    <StyledTitle variant="h6" gutterBottom sx={{ ml: 3 }}>Manage Admins</StyledTitle>
                    <StyledTableContainer component={Paper}>
                        <Table
                            sx={{
                                width: '100%',
                                overflowX: 'auto',
                                '& th': {
                                    fontWeight: 'bold',
                                    backgroundColor: '#f4f4f4',
                                },
                                '& td, & th': {
                                    padding: '12px',
                                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <StyledTableCell>Name</StyledTableCell>
                                    <StyledTableCell>Email</StyledTableCell>
                                    <StyledTableCell>Password</StyledTableCell>
                                    <StyledTableCell align="center">Actions</StyledTableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {admins.map((admin) => (
                                    <TableRow key={admin.email} hover>
                                        <TableCell>{admin.name}</TableCell>
                                        <TableCell>{admin.email}</TableCell>
                                        <TableCell>
                                            {passwordVisibility[admin.email] ? admin.password : "••••••••"}
                                            <IconButton onClick={() => togglePasswordVisibility(admin.email)}>
                                                {passwordVisibility[admin.email] ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{
                                                display: 'flex',
                                                flexDirection: { xs: 'column', sm: 'row' }, // Stack on smaller screens
                                                justifyContent: 'center',
                                                gap: 1,
                                            }}>
                                                <StyledButton
                                                    color="primary"
                                                    onClick={() => setEditAdmin({ ...admin })}
                                                    sx={{
                                                        width: { xs: '100%', sm: '80px' }, // Full width on smaller screens
                                                        fontSize: '0.8rem',
                                                        padding: '5px',
                                                        '&:hover': {
                                                            backgroundColor: 'primary.dark',
                                                            color: '#000',
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                                                        },
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    Edit
                                                </StyledButton>
                                                <StyledButton
                                                    color="error"
                                                    onClick={() => handleOpenDeleteConfirmation(admin.email, admin.name)}
                                                    sx={{
                                                        width: { xs: '100%', sm: '80px' }, // Full width on smaller screens
                                                        fontSize: '0.8rem',
                                                        padding: '5px',
                                                        '&:hover': {
                                                            backgroundColor: 'primary.dark',
                                                            color: '#000',
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                                                        },
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    Delete
                                                </StyledButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>

                    {editAdmin && (
                        <StyledPaper>
                            <StyledTitle variant="h6" gutterBottom>Update Admin Password</StyledTitle>
                            <StyledTextField label="New Password" name="password" type="password" fullWidth margin="normal" value={editAdmin.password || ""} onChange={handleEditAdminChange} />
                            <StyledButton variant="contained" color="primary" onClick={updateAdminPassword} fullWidth>Update Password</StyledButton>
                        </StyledPaper >
                    )}
                </Grid >
            </Grid >

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={handleCloseSnackbar}>
                <StyledAlert severity={snackbar.severity} onClose={handleCloseSnackbar}>{snackbar.message}</StyledAlert >
            </Snackbar >

            <Dialog
                open={deleteConfirmation.open}
                onClose={handleCloseDeleteConfirmation}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{"Confirm Delete"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {`Are you sure you want to delete ${deleteConfirmation.name}?`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteConfirmation} color="primary">
                        No
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" autoFocus>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
        </StyledContainer >
    );
}
